// app.users — Cognito User Pool admin wrapper, scoped to ONE app's
// pool. Works directly from the browser as long as the caller's STS
// session holds IAM perms for the corresponding `cognito-idp:Admin*`
// actions on the pool ARN. Baselines:
//   - `{slug}:admin` (via the `manage-users` capability) and `{slug}:owner`
//     have the perms granted via renderAdminPolicy / renderOwnerPolicy.
//   - Other users hitting these methods get AccessDenied from Cognito.

import {
  AdminAddUserToGroupCommand,
  AdminCreateUserCommand,
  AdminDeleteUserCommand,
  AdminDisableUserCommand,
  AdminEnableUserCommand,
  AdminGetUserCommand,
  AdminListGroupsForUserCommand,
  AdminRemoveUserFromGroupCommand,
  AdminResetUserPasswordCommand,
  AdminSetUserPasswordCommand,
  CognitoIdentityProviderClient,
  ListUsersCommand,
  ListUsersInGroupCommand,
  type UserType,
} from "https://cdn.jsdelivr.net/npm/@aws-sdk/client-cognito-identity-provider@3.1054.0/+esm";
import { appRoleGroupName, appRoleLocalName, isAppRoleGroup } from "./policy-templates.ts";

export interface UsersConfig {
  region: string;
  userPoolId: string;
  /** The project slug — group names are `{slug}:{role}`. Required by
   *  `assignRole` to tell app-role groups from tiers/operator groups. */
  slug: string;
  /** Per-request credentials provider — must return the caller's STS
   *  session (e.g. `() => platform.getCredentials()`). */
  credentials: () => Promise<{
    accessKeyId: string;
    secretAccessKey: string;
    sessionToken?: string;
  }>;
}

export interface UserSummary {
  sub: string;
  username: string;
  email?: string;
  enabled: boolean;
  status?: string;
  groups?: string[];
  createdAt?: Date;
  updatedAt?: Date;
}

export interface UsersPage {
  users: UserSummary[];
  nextToken?: string;
}

export interface InviteArgs {
  /** Email-formatted username — Cognito uses this as both username and
   *  email attribute. The pool must be configured with email as the
   *  username attribute (bootstrap-platform does this). */
  email: string;
  /** Cognito groups to add the user to after creation. */
  groups?: string[];
  /** Optional explicit temp password. Generated if omitted. */
  temporaryPassword?: string;
  /** If true, Cognito sends the standard welcome email with the temp
   *  password. If false, no email — caller delivers the password
   *  out-of-band. Default true. */
  sendInvite?: boolean;
}

export interface UsersModule {
  list(opts?: { groupName?: string; pageToken?: string; limit?: number }): Promise<UsersPage>;
  get(sub: string): Promise<UserSummary | null>;
  invite(args: InviteArgs): Promise<UserSummary>;
  assignGroup(sub: string, group: string): Promise<void>;
  /** Set the user's ONE app role (PERMISSIONS-SPEC §1.3): remove every other
   *  `{slug}:role:*` group, THEN add the new one — in that order, so a
   *  failure mid-way leaves the user with fewer privileges, never two roles
   *  (the Identity Pool resolves one IAM role; two app-role groups make the
   *  outcome priority-ordering luck). Tiers (`{slug}:admin`…) and operator
   *  groups (`{slug}:operator-*`) are STRUCTURALLY out of reach — app roles
   *  live in their own `{slug}:role:` namespace, so the sweep can't touch a
   *  tier even on the ROOT project, where the own pool IS the platform pool
   *  and tier groups sit on the same users. Accepts the local role name
   *  (`"editor"`) or the full group (`"{slug}:role:editor"`); `null` clears
   *  the app role (back to plain member). Returns the groups removed. */
  assignRole(sub: string, role: string | null): Promise<{ removed: string[] }>;
  removeGroup(sub: string, group: string): Promise<void>;
  listGroupsFor(sub: string): Promise<string[]>;
  disable(sub: string): Promise<void>;
  enable(sub: string): Promise<void>;
  delete(sub: string): Promise<void>;
  /** Emailed-code reset (AdminResetUserPassword). NEVER call on a
   *  handle-mode (email-less) pool: the account enters RESET_REQUIRED waiting
   *  for a code that can't arrive — bricked until an admin setPassword. Use
   *  `setPassword` there instead. */
  resetPassword(sub: string): Promise<void>;
  setPassword(sub: string, password: string, opts?: { permanent?: boolean }): Promise<void>;
}

export function createUsers(cfg: UsersConfig): UsersModule {
  const client = new CognitoIdentityProviderClient({
    region: cfg.region,
    credentials: async () => cfg.credentials(),
  });
  const poolId = cfg.userPoolId;

  function toSummary(u: UserType): UserSummary {
    const attrs = Object.fromEntries((u.Attributes ?? []).map((a) => [a.Name, a.Value]));
    return {
      sub: attrs.sub ?? u.Username ?? "",
      username: u.Username ?? "",
      email: attrs.email,
      enabled: u.Enabled ?? true,
      status: u.UserStatus,
      createdAt: u.UserCreateDate,
      updatedAt: u.UserLastModifiedDate,
    };
  }

  return {
    async list(opts = {}) {
      if (opts.groupName) {
        const res = await client.send(
          new ListUsersInGroupCommand({
            UserPoolId: poolId,
            GroupName: opts.groupName,
            NextToken: opts.pageToken,
            Limit: opts.limit ?? 60,
          }),
        );
        return {
          users: (res.Users ?? []).map(toSummary),
          nextToken: res.NextToken,
        };
      }
      const res = await client.send(
        new ListUsersCommand({
          UserPoolId: poolId,
          PaginationToken: opts.pageToken,
          Limit: opts.limit ?? 60,
        }),
      );
      return {
        users: (res.Users ?? []).map(toSummary),
        nextToken: res.PaginationToken,
      };
    },

    async get(sub: string) {
      try {
        const res = await client.send(
          new AdminGetUserCommand({ UserPoolId: poolId, Username: sub }),
        );
        const groups = await this.listGroupsFor(sub).catch(() => []);
        return {
          ...toSummary({
            Username: res.Username,
            Attributes: res.UserAttributes,
            Enabled: res.Enabled,
            UserStatus: res.UserStatus,
            UserCreateDate: res.UserCreateDate,
            UserLastModifiedDate: res.UserLastModifiedDate,
          }),
          groups,
        };
      } catch (e) {
        if ((e as { name?: string }).name === "UserNotFoundException") return null;
        throw e;
      }
    },

    async invite(args: InviteArgs) {
      const tempPassword = args.temporaryPassword ?? generateTempPassword();
      const created = await client.send(
        new AdminCreateUserCommand({
          UserPoolId: poolId,
          Username: args.email,
          TemporaryPassword: tempPassword,
          MessageAction: args.sendInvite === false ? "SUPPRESS" : undefined,
          DesiredDeliveryMediums: args.sendInvite === false ? undefined : ["EMAIL"],
          UserAttributes: [
            { Name: "email", Value: args.email },
            { Name: "email_verified", Value: "true" },
          ],
        }),
      );
      const sub =
        (created.User?.Attributes ?? []).find((a) => a.Name === "sub")?.Value ?? args.email;
      for (const group of args.groups ?? []) {
        await client.send(
          new AdminAddUserToGroupCommand({
            UserPoolId: poolId,
            Username: args.email,
            GroupName: group,
          }),
        );
      }
      return {
        ...toSummary(created.User ?? { Username: args.email }),
        sub,
        groups: args.groups ?? [],
      };
    },

    async assignGroup(sub: string, group: string) {
      await client.send(
        new AdminAddUserToGroupCommand({
          UserPoolId: poolId,
          Username: sub,
          GroupName: group,
        }),
      );
    },

    async assignRole(sub: string, role: string | null) {
      const slug = cfg.slug;
      // Accept the local name ("editor") or the full group
      // ("{slug}:role:editor"). Structural, no name list: app roles live in
      // the `{slug}:role:` namespace, so ANY name is a valid app role — and a
      // full group name OUTSIDE that namespace (a tier, an operator group)
      // can't be expressed as a target at all.
      let local: string | null;
      if (role === null) local = null;
      else if (role.includes(":")) {
        local = appRoleLocalName(slug, role);
        if (local === null) {
          throw new Error(
            `app.users.assignRole: '${role}' is not one of '${slug}''s app-role groups ` +
              `(${appRoleGroupName(slug, "{name}")}) — tiers and operator groups are ` +
              "managed from the Console users panel, not assignRole.",
          );
        }
      } else local = role;
      const target = local === null ? null : appRoleGroupName(slug, local);
      const current = await this.listGroupsFor(sub);
      const removed: string[] = [];
      // Remove first, add second (fail-closed: never two app roles).
      for (const group of current) {
        if (group === target || !isAppRoleGroup(slug, group)) continue;
        await client.send(
          new AdminRemoveUserFromGroupCommand({
            UserPoolId: poolId,
            Username: sub,
            GroupName: group,
          }),
        );
        removed.push(group);
      }
      if (target && !current.includes(target)) {
        await client.send(
          new AdminAddUserToGroupCommand({
            UserPoolId: poolId,
            Username: sub,
            GroupName: target,
          }),
        );
      }
      return { removed };
    },

    async removeGroup(sub: string, group: string) {
      await client.send(
        new AdminRemoveUserFromGroupCommand({
          UserPoolId: poolId,
          Username: sub,
          GroupName: group,
        }),
      );
    },

    async listGroupsFor(sub: string) {
      const res = await client.send(
        new AdminListGroupsForUserCommand({ UserPoolId: poolId, Username: sub }),
      );
      return (res.Groups ?? []).map((g) => g.GroupName ?? "").filter(Boolean);
    },

    async disable(sub: string) {
      await client.send(new AdminDisableUserCommand({ UserPoolId: poolId, Username: sub }));
    },

    async enable(sub: string) {
      await client.send(new AdminEnableUserCommand({ UserPoolId: poolId, Username: sub }));
    },

    async delete(sub: string) {
      await client.send(new AdminDeleteUserCommand({ UserPoolId: poolId, Username: sub }));
    },

    async resetPassword(sub: string) {
      await client.send(
        new AdminResetUserPasswordCommand({ UserPoolId: poolId, Username: sub }),
      );
    },

    async setPassword(sub: string, password: string, opts = {}) {
      await client.send(
        new AdminSetUserPasswordCommand({
          UserPoolId: poolId,
          Username: sub,
          Password: password,
          Permanent: opts.permanent ?? false,
        }),
      );
    },
  };
}

function generateTempPassword(): string {
  const alphabet = "ABCDEFGHJKLMNPQRSTUVWXYZ23456789";
  const bytes = new Uint8Array(16);
  globalThis.crypto.getRandomValues(bytes);
  let out = "";
  for (const b of bytes) out += alphabet[b % alphabet.length];
  return `${out}!a`;
}
